// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — the `spaces` route body (PR-B, behind the ABB-S6 seam)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `spaces` route projector: model space + each paper-space layout, PAGINATED / SORTED / FIELD-
// projected. A DOCUMENT-LEVEL inventory — NOT space-scoped (`isDocumentLevelRoute`). Registered via
// `registerRouteProjector('spaces', projectSpaces)`.
//
// DERIVED facets: `isActive` (spaceId === activeSpaceId — a context switch flips it), `entityCount`
// (entities in the space), `entityBounds` (union WCS extent of the space's entities, ABSENT when none
// computable). So the binding tuple spans `{ geometryRevision, tableRevision, contextRevision }`:
// entityCount/entityBounds are geometry-derived (fullstack:966), the layout table/plot is table-
// derived, and `isActive` is context-derived (an active-space switch bumps `contextRevision` only).
// `snapshotRevision` rides as the page ANCHOR only (INV-9).
//
// DEFAULT ordering (no `?sort`): model space FIRST → layouts by `tabOrder` ASC → `spaceId` tie-break
// (fullstack:966). An explicit `?sort` overrides it.
//
// LEAF purity (ПРИОРИТЕТ #2): imports ONLY the seam + the shared helpers. depcruise cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import {
  type EntityBoundsExtent,
  type RouteProjector,
  type SceneSnapshotRef,
  type SnapshotSpaceRef,
} from "../sceneSummaryProjector";
import {
  compareFacetsBy,
  paginate,
  parseKeyDirSort,
  projectFacetFields,
  resolveProjectionMode,
  unionEntityBounds,
} from "./projectionHelpers";

/** The LEAN CORE field set projected when `?fields` is ABSENT. Every member is allowlisted. */
const LEAN_CORE_FIELDS = [
  "spaceId",
  "kind",
  "name",
  "isActive",
  "entityCount",
  "tabOrder",
] as const satisfies readonly (keyof SnapshotSpaceRef)[];

/** The legal `key:dir` sort KEYS (`type` sorts by `kind` — the contract names `type/kind`). */
const SPACE_SORT_KEYS = ["name", "type"] as const;
type SpaceSortKey = (typeof SPACE_SORT_KEYS)[number];

/**
 * A space record with its DERIVED `isActive` / `entityCount` / `entityBounds` resolved (the projected
 * row). The base facet's `isActive`/`entityCount` are REQUIRED (the caller may pre-fill them, but the
 * body re-derives from the live entity walk to keep the geometry-derived binding honest);
 * `entityBounds` stays OPTIONAL (absent ⇔ no computable bounds).
 */
type SpaceRow = SnapshotSpaceRef;

/**
 * The CHEAP per-space entity COUNT ONLY (O(entities) once, `e.spaceId` only — never touches the
 * expensive `entityBounds` lazy getter). `entityCount` is part of the LEAN default listing (always
 * needed, regardless of `?fields`/pagination), so this pass ALWAYS runs, independent of whether
 * `entityBounds` was requested or which rows survive pagination.
 */
function countEntitiesPerSpace(snapshot: SceneSnapshotRef): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const e of snapshot.entities) counts.set(e.spaceId, (counts.get(e.spaceId) ?? 0) + 1);
  return counts;
}

/**
 * The EXPENSIVE per-space UNION `entityBounds`, scoped to ONLY `spaceIds` — @codex T1 arm(c)
 * round-13 P2 finding: the prior `buildSpaceGeometry` touched `e.entityBounds` (the LAZY, MEMOIZED
 * `computeEntityWcsBounds` getter — recursive block resolution, curve tessellation) for EVERY
 * entity in the WHOLE DOCUMENT before sorting/pagination even ran, so a `spaces?fields=
 * entityBounds&limit=1` read (wanting just ONE row) still forced this cost across every OTHER,
 * unreturned space too. Sorting/pagination for `spaces` depends ONLY on `name`/`kind`/`tabOrder`/
 * `spaceId` (`spaceSortValue`/`defaultOrderCompare` below — never `entityBounds`/`entityCount`),
 * so the caller can safely SORT + PAGINATE first (using the cheap `countEntitiesPerSpace` pass for
 * `entityCount`), then call this ONLY for the spaceIds in the final paginated slice — a `limit=1`
 * read then touches `entityBounds` for ONLY that one space's entities, not the whole document.
 */
function unionEntityBoundsForSpaces(snapshot: SceneSnapshotRef, spaceIds: ReadonlySet<string>): ReadonlyMap<string, EntityBoundsExtent> {
  const bounds = new Map<string, EntityBoundsExtent>();
  for (const e of snapshot.entities) {
    if (!spaceIds.has(e.spaceId)) continue;
    // Read the getter EXACTLY ONCE per entity into a local (not `e.entityBounds !== undefined`
    // followed by a second `e.entityBounds` read) — the real captureSnapshot.ts getter is
    // memoized so a double read is harmless there, but reading once is strictly cheaper and
    // keeps this call-count independent of any future non-memoized bounds source.
    const eb = e.entityBounds;
    if (eb !== undefined) {
      const prior = bounds.get(e.spaceId);
      bounds.set(e.spaceId, prior === undefined ? eb : unionEntityBounds(prior, eb));
    }
  }
  return bounds;
}

/**
 * Extract a space row's value for a sort key: `name` is the RAW name (string); `type` sorts by `kind`
 * (the contract's `type/kind`). The seam validated membership; an unexpected key → undefined.
 */
function spaceSortValue(row: SpaceRow, key: SpaceSortKey): string | number | undefined {
  switch (key) {
    case "name":
      return row.name;
    case "type":
      return row.kind;
    default:
      return undefined;
  }
}

/**
 * The DEFAULT (no-`?sort`) comparator: model space FIRST → layouts by `tabOrder` ASC → `spaceId`
 * tie-break (fullstack:966). Model is `kind === "model"`; everything else orders by tabOrder then id.
 * STABLE for equal keys (preserves table order on a full tie).
 */
function defaultOrderCompare(a: SpaceRow, b: SpaceRow): number {
  const aModel = a.kind === "model" ? 0 : 1;
  const bModel = b.kind === "model" ? 0 : 1;
  if (aModel !== bModel) {
    return aModel - bModel; // model space sorts first.
  }
  const aTab = a.tabOrder ?? 0;
  const bTab = b.tabOrder ?? 0;
  if (aTab !== bTab) {
    return aTab - bTab; // then by tab order ASC.
  }
  if (a.spaceId === b.spaceId) {
    return 0;
  }
  return a.spaceId < b.spaceId ? -1 : 1; // spaceId tie-break.
}

/**
 * The `spaces` route body. enumerate-spaces → attach derived isActive/entityCount/entityBounds → sort
 * (default model-first, or explicit `?sort`) → (fullField | field-project) → paginate. Binds
 * `routeRevisions` to `{ geometryRevision, tableRevision, contextRevision }` (entityCount/bounds
 * geometry-derived; layout table/plot table-derived; isActive context-derived). `snapshotRevision`
 * rides as the page ANCHOR only. `resolvedSpaceId: null` (document-level). byteTruncated: false.
 */
export const projectSpaces: RouteProjector = ({ query, cursor, snapshot }) => {
  // S7 BOUNDARY: the layout PLOT/LIMITS/EXTENTS facets (`pageSetupName`/`paperName`/`plotStyleName` (plot),
  // `minimumLimits`/`maximumLimits` (limits), `minimumExtents`/`maximumExtents` (extents); fullstack:966) are
  // PRE-COMPUTED snapshot facets S7's capture closure populates from `layoutSchema`/`layoutSchema.plotSettings`
  // — the body only PROJECTS them (they ride through the `...rest` spread below, projected by `projectFacetFields`
  // when requested via `?fields`/`?fullField`). Their absence in a PR-B fixture is NOT a bug: a model space (or a
  // layout with no plot config) legitimately omits them, and `projectFacetFields` omits an absent optional. The
  // byte-cap + `*Truncated` flags on any long plot STRING are applied AT CAPTURE (fullstack:990), not here. Only
  // `isActive`/`entityCount`/`entityBounds` are body-DERIVED (below — the geometry-derived fields are re-derived
  // from the live entity walk, the captured copies STRIPPED, so the `geometryRevision` binding stays honest).
  //
  // PROJECTION-MODE + LIMIT resolved FIRST (before the entity walk — @codex T1 arm(c) round-4 P2
  // finding): `?fullField` forces limit=1 + projects only that field; else lean/`?fields`. Needed
  // up front so the EXPENSIVE bounds pass (below, AFTER pagination — round-13 finding) can gate on
  // whether the caller's resolved field set actually includes `entityBounds`.
  const { fields, limit } = resolveProjectionMode(query, LEAN_CORE_FIELDS);
  const needsBounds = fields.includes("entityBounds");

  // ENUMERATE all spaces (document-level — `?scope` does NOT filter). Attach the DERIVED isActive
  // (vs the snapshot's activeSpaceId) + the CHEAP entityCount (from `countEntitiesPerSpace` — O(entities)
  // once, never touches the expensive `entityBounds` lazy getter). `entityBounds` is attached LATER,
  // AFTER sort+pagination, scoped to only the returned rows (@codex T1 arm(c) round-13 P2 finding —
  // see `unionEntityBoundsForSpaces`'s doc).
  const counts = countEntitiesPerSpace(snapshot);
  const rows: SpaceRow[] = snapshot.spaces.map((space) => {
    // The CURRENT entity-walk derivation is the SINGLE AUTHORITATIVE source for the geometry-derived
    // `entityCount`/`entityBounds` (they bind `geometryRevision`). DESTRUCTURE the captured facet to
    // STRIP any S7-captured `entityCount`/`entityBounds` (`...rest` excludes both) — a bare `...space`
    // spread would LEAK a stale captured `entityBounds` past a CURRENT empty derivation (PR-B @codex
    // round-3, finding #3): the old conditional-spread only OVERWROTE when the derived bound existed, so
    // a space whose captured facet carried `entityBounds` but whose current walk yields NONE would still
    // report the stale captured bound under `?fields=entityBounds` instead of OMITTING it (boundsKnown=
    // false). Re-attaching ONLY the freshly-derived `entityCount` here makes the row's geometry fields
    // reflect the current derivation exclusively; `entityBounds` is attached after pagination below.
    const { entityCount: _capturedCount, entityBounds: _capturedBounds, ...rest } = space;
    return {
      ...rest,
      isActive: space.spaceId === snapshot.activeSpaceId,
      entityCount: counts.get(space.spaceId) ?? 0,
    };
  });

  // SORT. No `?sort` → the DEFAULT model-first / tabOrder / spaceId order; an explicit `?sort` overrides.
  // An explicit `?sort=type` (by `kind`) gets a `name` ASC tie-break so equal-kind spaces resolve to a
  // deterministic documented order (parity with the layers/blocks table routes), not the DXF list order.
  // Neither comparator reads `entityBounds`/`entityCount` (`spaceSortValue`/`defaultOrderCompare`'s own
  // doc) — sorting is safe to run BEFORE the expensive bounds pass.
  const parsedSort = parseKeyDirSort<SpaceSortKey>(query.sort, SPACE_SORT_KEYS);
  const ordered =
    parsedSort === null
      ? [...rows].sort(defaultOrderCompare)
      : [...rows].sort((a, b) =>
          compareFacetsBy(a, b, parsedSort.key, parsedSort.dir, spaceSortValue, "name"),
        );

  // PAGINATION — the SHARED paginate primitive.
  const { slice, nextOffsetKey } = paginate(ordered, cursor, limit);

  // @codex T1 arm(c) round-13 P2 finding: the EXPENSIVE `entityBounds` union now runs AFTER
  // pagination, scoped to ONLY the paginated slice's space ids — a `?fields=entityBounds&limit=1`
  // read touches the lazy `computeEntityWcsBounds` getter for just that ONE space's entities, not
  // the whole document's every space.
  const boundedSlice: readonly SpaceRow[] = needsBounds
    ? (() => {
        const boundsById = unionEntityBoundsForSpaces(snapshot, new Set(slice.map((row) => row.spaceId)));
        return slice.map((row) => {
          const b = boundsById.get(row.spaceId);
          // entityBounds present ONLY when the CURRENT walk has a computable union bound (else
          // OMITTED — the empty/unknown marker). `{}` omits, NOT `{ entityBounds: undefined }`.
          return b === undefined ? row : { ...row, entityBounds: b };
        });
      })()
    : slice;

  // FIELD PROJECTION (INV-1). An OPTIONAL field absent on the row is omitted (entityBounds when
  // undefined, tabOrder/handle/etc when a model space lacks them).
  const items = boundedSlice.map((row) => projectFacetFields(row as unknown as Record<string, unknown>, fields));

  return {
    items,
    nextOffsetKey,
    // ROUTE-SPECIFIC binding ([L7] / fullstack:966): entityCount/entityBounds geometry-derived
    // (geometryRevision); the layout table/plot table-derived (tableRevision); isActive context-derived
    // (contextRevision — an active-space switch bumps it). `snapshotRevision` is EXCLUDED (rides as the
    // page ANCHOR only).
    routeRevisions: {
      geometryRevision: snapshot.revisions.geometryRevision,
      tableRevision: snapshot.revisions.tableRevision,
      contextRevision: snapshot.revisions.contextRevision,
    },
    resolvedSpaceId: null, // document-level — not space-scoped.
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false,
    projectionVersion: snapshot.projectionVersion,
    total: ordered.length,
  };
};
